/**
 * BuilderV2 — Chat-first, light-themed builder layout.
 * Iris on the left, full-screen preview on the right.
 * Replaces the old sidebar+tabs paradigm.
 *
 * This is a wrapper that uses the existing BuilderPage state/logic
 * but with a completely new UI layout.
 */
import { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, Check, Globe, ExternalLink, Sparkles, RotateCcw, Loader2, Monitor, Smartphone } from 'lucide-react';
import IrisChat from './IrisChat';
import BuilderOnboarding from './BuilderOnboarding';
import { PhotosSheet, ColorsSheet, FontsSheet } from './WorkflowSheets';
import { FONT_PAIRINGS } from './constants';

const BuilderV2Layout = memo(({
  // Core data
  site, siteId, activePageData, activeHtml, activePage,
  // Navigation
  navigate, pages,
  // Chat
  chatHistory, sending, handleFullChat, setChatHistory,
  // Actions
  handleSave, saveStatus, hasUnsavedChanges, handlePublish, publishing, publishStatus,
  handleUndo, undoStack, setShowRegenConfirm, regenerating,
  // UI state
  previewMode, setPreviewMode, setActivePage,
  // Modals
  setShowSEO, setShowCopyQ, setShowPricing,
  // Plan
  userPlan, usageStats,
  // Quick actions
  onQuickAction,
  // Site state for suggestions
  siteState,
  // Media/Colors/Fonts for workflow sheets
  mediaItems, onMediaSwap, onMediaUpload,
  colors, onColorSwap,
  fonts, onFontSwap,
  // Onboarding
  isOnboarding, onOnboardingComplete,
}) => {
  const [activeSheet, setActiveSheet] = useState(null); // null | 'photos' | 'colors' | 'fonts'
  const [editFlash, setEditFlash] = useState(false);
  const [iframeFade, setIframeFade] = useState(1); // 1 = visible, 0 = hidden (crossfade)
  const prevHtmlRef = useRef(activeHtml);
  const iframeRef = useRef(null);

  // Crossfade + flash + scroll-to-section when HTML changes (edit applied)
  useEffect(() => {
    if (prevHtmlRef.current && activeHtml && prevHtmlRef.current !== activeHtml) {
      // Step 1: Start crossfade — dim iframe
      setIframeFade(0.3);
      setEditFlash(true);

      // Step 2: After iframe reloads with new HTML, fade back in + scroll to first section
      const fadeTimer = setTimeout(() => {
        setIframeFade(1);
        // Try to scroll iframe to the first visible section (heuristic: first <section> tag)
        try {
          const iframe = iframeRef.current;
          if (iframe?.contentDocument) {
            const sections = iframe.contentDocument.querySelectorAll('section');
            if (sections.length > 1) {
              // Scroll to the second section (first is usually hero, which is visible by default)
              sections[1].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        } catch { /* cross-origin or sandbox restriction — ignore */ }
      }, 400);

      const flashTimer = setTimeout(() => setEditFlash(false), 1200);
      return () => { clearTimeout(fadeTimer); clearTimeout(flashTimer); };
    }
    prevHtmlRef.current = activeHtml;
  }, [activeHtml]);
  const handleSendMessage = useCallback((msg) => {
    setChatHistory(prev => [...prev, { role: 'user', message: msg }]);
    handleFullChat(msg);
  }, [handleFullChat, setChatHistory]);

  const handleQuickActionWrap = useCallback((action) => {
    if (action === 'seo_audit') { setShowSEO(true); return; }
    if (action === 'personalize_copy') { setShowCopyQ(true); return; }
    if (action === 'swap_photos') { setActiveSheet('photos'); return; }
    if (action === 'change_colors') { setActiveSheet('colors'); return; }
    if (action === 'change_fonts') { setActiveSheet('fonts'); return; }
    if (action === 'publish') { handlePublish(); return; }
    if (action === 'continue') { return; } // dismiss suggestion
    if (action === 'write_blogs') { handleSendMessage("Generate blog topic ideas for my site"); return; }
    if (action === 'replace_hero') { handleSendMessage("Replace the hero background image with something more cinematic"); return; }
    if (action === 'replace_gallery') { handleSendMessage("Replace the gallery images with different wedding photos"); return; }
    if (action === 'add_page') { handleSendMessage("Add a new page to my site"); return; }
    if (action === 'connect_domain') { handleSendMessage("How do I connect my custom domain?"); return; }
    if (onQuickAction) onQuickAction(action);
  }, [setShowSEO, setShowCopyQ, handleSendMessage, handlePublish, onQuickAction]);

  return (
    <div className="builder-v2" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#faf8f5', color: '#2a2725', fontFamily: 'var(--sans)' }}>
      {/* Minimal Topbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', height: '52px', borderBottom: '1px solid #e8e3dc', flexShrink: 0, background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '8px', transition: 'background .15s' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f0ece6'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <ArrowLeft size={18} />
          </button>
          <span style={{ fontSize: '16px', fontWeight: 600 }}>{site?.business_name || 'My Site'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Edit counter */}
          {usageStats && usageStats.limit !== 'unlimited' && (
            <span data-testid="edit-counter" style={{ fontSize: '11px', color: usageStats.used >= usageStats.limit * 0.8 ? '#c44848' : '#8c8782', fontFamily: 'var(--sans)', padding: '4px 10px', background: usageStats.used >= usageStats.limit * 0.8 ? 'rgba(196,72,72,0.06)' : '#f5f2ee', borderRadius: '6px' }}>
              {usageStats.limit - usageStats.used} edits left
            </span>
          )}
          {/* Unsaved indicator */}
          {hasUnsavedChanges && saveStatus !== 'saved' && (
            <span style={{ fontSize: '11px', color: '#C9A84C', fontFamily: 'var(--sans)' }}>Unsaved</span>
          )}
          {undoStack.length > 0 && (
            <button onClick={handleUndo} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#C9A84C', background: 'rgba(201,168,76,0.04)', padding: '7px 14px', cursor: 'pointer', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', transition: 'all .15s' }}>
              <RotateCcw size={14} /> Undo
            </button>
          )}
          <button onClick={() => setShowRegenConfirm(true)} disabled={regenerating} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#8c8782', background: 'none', padding: '7px 14px', cursor: 'pointer', border: '1px solid #e8e3dc', borderRadius: '8px', transition: 'all .15s' }}>
            {regenerating ? <><Loader2 size={14} className="animate-spin" /> Regenerating...</> : <><Sparkles size={14} /> New Look</>}
          </button>
          <button onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: saveStatus === 'saved' ? '#5ba89d' : '#8c8782', background: 'none', border: `1px solid ${saveStatus === 'saved' ? 'rgba(91,168,157,0.25)' : '#e8e3dc'}`, padding: '7px 14px', cursor: 'pointer', borderRadius: '8px', transition: 'all .2s' }}>
            {saveStatus === 'saved' ? <><Check size={14} /> Saved</> : <><Save size={14} /> Save</>}
          </button>
        </div>
      </div>

      {/* Regeneration banner — non-blocking, shows below topbar */}
      {regenerating && (
        <div data-testid="regen-banner" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          padding: '8px 20px', background: 'rgba(201,168,76,0.06)',
          borderBottom: '1px solid rgba(201,168,76,0.12)', flexShrink: 0,
        }}>
          <Loader2 size={14} className="animate-spin" style={{ color: '#C9A84C' }} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8B7432', fontWeight: 500 }}>
            Iris is generating a new look — you can keep editing while you wait
          </span>
        </div>
      )}

      {/* Main content: Chat/Onboarding + Preview */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* LEFT: Onboarding (inline) or Iris Chat */}
        {isOnboarding ? (
          <BuilderOnboarding
            siteId={siteId}
            siteName={site?.business_name}
            businessType={site?.business_type}
            onComplete={onOnboardingComplete}
          />
        ) : (
          <IrisChat
            chatHistory={chatHistory}
            sending={sending}
            onSendMessage={handleSendMessage}
            onQuickAction={handleQuickActionWrap}
            siteState={siteState}
            userPlan={userPlan}
            onUpgrade={() => setShowPricing(true)}
            onPublish={handlePublish}
            publishing={publishing}
            publishStatus={publishStatus}
            onUndo={handleUndo}
            canUndo={undoStack.length > 0}
          />
        )}

        {/* RIGHT: Preview */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f0ece6' }}>
          {/* Page tabs + device toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', background: '#faf8f5', borderBottom: '1px solid #e8e3dc', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '4px', overflow: 'auto' }}>
              {(pages || []).map(p => (
                <button key={p.id} onClick={() => setActivePage(p.id)} style={{
                  padding: '6px 14px', fontSize: '12px', fontWeight: activePage === p.id ? 600 : 400,
                  background: activePage === p.id ? '#fff' : 'transparent',
                  border: 'none', cursor: 'pointer', borderRadius: '8px',
                  color: activePage === p.id ? '#2a2725' : '#8c8782',
                  boxShadow: activePage === p.id ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                  transition: 'all .15s',
                }}>
                  {p.name}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '2px', background: '#f0ece6', padding: '3px', borderRadius: '8px' }}>
              <button onClick={() => setPreviewMode('desktop')} style={{
                padding: '4px 10px', background: previewMode === 'desktop' ? '#fff' : 'transparent',
                border: 'none', cursor: 'pointer', color: previewMode === 'desktop' ? '#C9A84C' : '#8c8782',
                borderRadius: '6px', boxShadow: previewMode === 'desktop' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}><Monitor size={14} /></button>
              <button onClick={() => setPreviewMode('mobile')} style={{
                padding: '4px 10px', background: previewMode === 'mobile' ? '#fff' : 'transparent',
                border: 'none', cursor: 'pointer', color: previewMode === 'mobile' ? '#C9A84C' : '#8c8782',
                borderRadius: '6px', boxShadow: previewMode === 'mobile' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}><Smartphone size={14} /></button>
            </div>
          </div>

          {/* Preview card */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', position: 'relative' }}>
            <div style={{
              width: previewMode === 'mobile' ? '375px' : '100%',
              height: '100%', background: '#fff', borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden',
              transition: 'width .3s', position: 'relative',
            }}>
              {/* Browser chrome */}
              <div style={{ height: '36px', background: '#faf8f5', borderBottom: '1px solid #e8e3dc', display: 'flex', alignItems: 'center', padding: '0 12px', gap: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ff5f57' }} />
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#febd2e' }} />
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#28c840' }} />
                <span style={{ marginLeft: '8px', fontSize: '11px', color: '#8c8782' }}>
                  {site?.business_name?.toLowerCase().replace(/\s+/g, '')}.goldenhour.build
                </span>
              </div>
              {/* Iframe */}
              {activeHtml ? (
                <iframe
                  ref={iframeRef}
                  srcDoc={activeHtml + '<script>document.addEventListener("click",function(e){var a=e.target.closest("a");if(a){e.preventDefault();e.stopPropagation();var h=a.getAttribute("href");if(h&&h.startsWith("#")&&h.length>1){var t=document.querySelector(h);if(t)t.scrollIntoView({behavior:"smooth"});}return false;}},true);document.addEventListener("submit",function(e){e.preventDefault();},true);window.open=function(){return null;};<\/script>'}
                  title="Preview"
                  sandbox="allow-scripts allow-same-origin"
                  style={{ width: '100%', height: 'calc(100% - 36px)', border: 'none', opacity: iframeFade, transition: 'opacity 0.35s ease' }}
                  data-testid="builder-v2-preview"
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 36px)', color: '#8c8782', fontSize: '14px' }}>
                  Loading preview...
                </div>
              )}

              {/* Edit flash — golden pulse when site is updated */}
              {editFlash && (
                <div data-testid="edit-flash" style={{
                  position: 'absolute', inset: 0, top: '36px',
                  background: 'rgba(201,168,76,0.08)',
                  pointerEvents: 'none', zIndex: 5,
                  animation: 'editFlash 1.2s ease-out forwards',
                }} />
              )}

              {/* Workflow Sheets — slide up over the preview */}
              {activeSheet === 'photos' && (
                <PhotosSheet mediaItems={mediaItems || []} onSwap={onMediaSwap} onUpload={onMediaUpload} onClose={() => setActiveSheet(null)} />
              )}
              {activeSheet === 'colors' && (
                <ColorsSheet colors={colors || []} onSwap={onColorSwap} onClose={() => setActiveSheet(null)} />
              )}
              {activeSheet === 'fonts' && (
                <FontsSheet fonts={fonts || []} pairings={FONT_PAIRINGS} onSwap={onFontSwap} onClose={() => setActiveSheet(null)} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

BuilderV2Layout.displayName = 'BuilderV2Layout';
export default BuilderV2Layout;
